// src/pages/SignIn.tsx

import { useState } from "react";
import { Helmet } from "react-helmet";
import { supabase } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Link, Navigate } from "react-router-dom";
import { Mail, KeyRound, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAppContext } from "@/contexts/AppContext";

type Mode = "magic" | "password";

export default function SignIn() {
  const { session, loading: authLoading } = useAuth();
  const { isAdmin, loading: appLoading } = useAppContext();
  const [mode, setMode] = useState<Mode>("magic");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  // While auth is initializing — show spinner so we never flash the sign-in form
  if (authLoading || (session && appLoading)) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center text-gray-400">
        Loading…
      </div>
    );
  }

  // Already authenticated — route by role (profile has resolved)
  if (session) {
    return <Navigate to={isAdmin ? "/admin" : "/portal"} replace />;
  }

  const onMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const addr = email.trim();

    if (!addr) {
      toast({
        title: "Email required",
        description: "Please enter your email to receive a magic link.",
        variant: "destructive",
      });
      return;
    }

    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: addr,
        options: {
          // AdminRedirect in App.tsx will bounce admin users to /admin automatically
          emailRedirectTo: `${window.location.origin}/portal`,
        },
      });

      if (error) throw error;

      toast({
        title: "Magic link sent",
        description: "Check your inbox to finish signing in.",
      });
    } catch (err: any) {
      console.error("Magic link error:", err);
      toast({
        title: "Sign-in error",
        description: err.message ?? "Unable to send magic link.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const onPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const addr = email.trim();

    if (!addr || !password) {
      toast({
        title: "Missing credentials",
        description: "Please enter both email and password.",
        variant: "destructive",
      });
      return;
    }

    setBusy(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: addr,
        password,
      });


      if (error) throw error;

      // Safety: ensure profile exists
      if (data.user) {
        const { error: profileError } = await supabase
          .from("profiles")
          .upsert(
            { id: data.user.id, email: data.user.email },
            { onConflict: "id" }
          );

        if (profileError) {
          console.warn("Profile upsert error:", profileError);
        }
      }

      // Fetch profile role directly to decide destination without triggering auth mutex
      let destination = "/portal";
      try {
        const jwt = data.session!.access_token;
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/profiles?id=eq.${data.user!.id}&select=role`,
          {
            headers: {
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
              Authorization: `Bearer ${jwt}`,
            },
          }
        );
        if (res.ok) {
          const [row] = await res.json();
          if (row?.role === "admin") destination = "/admin";
        }
      } catch {
        // non-fatal — default to /portal
      }

      // Hard redirect so auth guards pick up the new session immediately
      window.location.href = destination;
    } catch (err: any) {
      console.error("Password sign-in error:", err);
      toast({
        title: "Sign-in error",
        description:
          err.message ?? "Unable to sign in with email & password.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const onForgotPassword = async () => {
    const addr = email.trim();
    if (!addr) {
      toast({
        title: "Enter your email",
        description: "Type your email then click Forgot password.",
      });
      return;
    }

    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(addr, {
        redirectTo: `${window.location.origin}/reset`,
      });

      if (error) throw error;

      toast({
        title: "Reset email sent",
        description: "Check your inbox to set a new password.",
      });
    } catch (err: any) {
      console.error("Reset password error:", err);
      toast({
        title: "Reset error",
        description: err.message ?? "Unable to send reset email.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Helmet>
      <title>Sign In — Fyrris</title>
      <meta name="robots" content="noindex" />
    </Helmet>
    <div className="min-h-screen bg-gray-950 text-gray-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-gray-900 border-gray-800 p-6">
        <h1 className="text-2xl font-semibold mb-2">Welcome back</h1>
        <p className="text-gray-400 mb-6">Sign in to your Fyrris portal.</p>

        <div className="mb-4 grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant={mode === "magic" ? "default" : "outline"}
            className="w-full"
            onClick={() => setMode("magic")}
            disabled={busy}
          >
            <Mail className="w-4 h-4 mr-2" />
            Magic Link
          </Button>
          <Button
            type="button"
            variant={mode === "password" ? "default" : "outline"}
            className="w-full"
            onClick={() => setMode("password")}
            disabled={busy}
          >
            <KeyRound className="w-4 h-4 mr-2" />
            Email &amp; Password
          </Button>
        </div>

        {mode === "magic" ? (
          <form onSubmit={onMagicLink} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-gray-950 border-gray-800"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-orange-600 hover:bg-orange-700"
              disabled={busy}
            >
              <Mail className="w-4 h-4 mr-2" />
              {busy ? "Sending…" : "Send Magic Link"}
            </Button>
          </form>
        ) : (
          <form onSubmit={onPassword} className="space-y-4">
            <div>
              <Label htmlFor="email2">Email</Label>
              <Input
                id="email2"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-gray-950 border-gray-800"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-gray-950 border-gray-800"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-orange-600 hover:bg-orange-700"
              disabled={busy}
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              {busy ? "Signing in…" : "Sign in"}
            </Button>

            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs text-gray-500">Having trouble?</span>
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto text-sm text-gray-300 hover:text-white"
                onClick={onForgotPassword}
                disabled={busy}
              >
                Forgot password?
              </Button>
            </div>
          </form>
        )}

        <div className="mt-6 text-sm text-gray-400">
          Not a Fyrris retailer yet?{" "}
          <Link to="/retailers/demo" className="text-orange-400 hover:underline">
            Book a demo
          </Link>
        </div>
      </Card>
    </div>
    </>
  );
}
